import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, BookOpen, Download } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal } from "../components/Motion";
import Seo from "../components/Seo";
import { ZR_SERIES, ZR_BANDS, ZR_UI } from "../i18n/zwischenraum";

const ZwischenraumBand = () => {
  const { band: slug } = useParams();
  const { lang } = useLang();
  const ui = ZR_UI[lang] || ZR_UI.de;
  const b = ZR_BANDS.find((x) => x.slug === slug);
  if (!b) return <Navigate to="/library/zwischenraum" replace />;

  const pdf = `/ebooks/${b.slug}.pdf`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "Book",
    name: b.title,
    bookFormat: "https://schema.org/EBook",
    inLanguage: "de",
    isPartOf: { "@type": "Book", name: ZR_SERIES.title },
    author: { "@type": "Organization", name: "MYSYMP AG" },
    description: b.desc,
  };

  return (
    <div data-testid={`zr-band-page-${b.slug}`} className="bg-obsidian">
      <Seo pageKey="library" path={`/library/zwischenraum/${b.slug}`} title={b.title} description={b.subtitle} schema={[schema]} />

      <section className="mx-auto max-w-[1400px] px-6 pt-40 pb-24 md:px-12 md:pt-52">
        <Link to="/library/zwischenraum" className="mb-12 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/45 transition-colors hover:text-champagne">
          <ArrowLeft size={14} /> {ui.back}
        </Link>

        <div className="grid gap-12 md:grid-cols-2 md:gap-16">
          <Reveal>
            <div className="relative mx-auto aspect-[3/4] w-full max-w-sm overflow-hidden border border-white/10">
              <img src={b.cover} alt={b.title} className="h-full w-full object-cover" />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="flex h-full flex-col justify-center">
              <span className="text-xs uppercase tracking-[0.3em] text-champagne">{ui.band} {b.n} · Zwischenraum-Geschichten</span>
              <h1 className="mt-4 font-serif text-4xl font-light leading-tight text-white md:text-5xl">{b.title}</h1>
              <p className="mt-4 font-serif text-lg font-light italic text-white/60">{b.subtitle}</p>
              <p className="mt-8 text-base font-light leading-relaxed text-white/70">{b.desc}</p>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link to={`/library/zwischenraum/${b.slug}/lesen`} data-testid="zr-band-read-online"
                  className="inline-flex items-center gap-2 bg-champagne px-7 py-3.5 text-xs uppercase tracking-[0.2em] text-obsidian transition-colors hover:bg-champagne-light">
                  <BookOpen size={15} /> {ui.readOnline}
                </Link>
                <a href={pdf} download data-testid="zr-band-download"
                  className="inline-flex items-center gap-2 border border-champagne/40 px-7 py-3.5 text-xs uppercase tracking-[0.2em] text-champagne transition-colors hover:bg-champagne/10">
                  <Download size={15} /> {ui.download}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ZwischenraumBand;
